'use client';

import Ratings from '@/components/shared/Ratings';
import { getRatingNumberAndType } from '@/utils/filters/getRatings';
import { formatMessage } from '@/utils/translationHelper';
import IconWithPopper from '@/components/card/IconWithPopper';
import type { Locale } from '@/i18n';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

interface Props {
  ratings?: string;
  reviews?: number;
  screenReaderText: string;
  showDistance?: boolean;
  distanceInMetric?: number;
  distanceInImperial?: number;
  measurementUnit?: string;
  locale: Locale;
  strings?: {
    csn_search_venue_card_distance_tooltip: string;
    csn_search_venue_card_distance_km: string;
    csn_search_venue_card_distance_mi: string;
  };
  isListViewInFullMap?: boolean;
}

interface distance {
  exists: boolean;
  value?: number;
  isMetricUnit?: boolean;
}

// Prepares a distance object.
function determineDistance(
  showDistance?: boolean,
  measurementUnit?: string,
  distanceInMetric?: number,
  distanceInImperial?: number
): distance {
  if (showDistance) {
    if (measurementUnit && measurementUnit === 'METRIC') {
      if (distanceInMetric && distanceInMetric > 0) {
        return { exists: true, value: Number(distanceInMetric.toFixed(1)), isMetricUnit: true };
      }
    } else if (distanceInImperial && distanceInImperial > 0) {
      return {
        exists: true,
        value: Number(distanceInImperial.toFixed(1)),
        isMetricUnit: false
      };
    }
  }
  return { exists: false };
}

function RatingReviewDistance({
  ratings,
  reviews,
  screenReaderText,
  measurementUnit,
  showDistance,
  distanceInMetric,
  distanceInImperial,
  locale,
  strings,
  isListViewInFullMap
}: Props): JSX.Element {
  const [ratingPopperOpen, setRatingPopperOpen] = useState(false);
  const ratingTriggerRef = useRef<HTMLDivElement>(null);
  const distance = determineDistance(showDistance, measurementUnit, distanceInMetric, distanceInImperial);
  const rating = useMemo(() => (ratings ? getRatingNumberAndType(ratings) : undefined), [ratings]);

  const onMouseEnterRatings = useCallback(() => {
    setRatingPopperOpen(true);
  }, []);

  const onMouseLeaveRatings = useCallback(() => {
    setRatingPopperOpen(false);
  }, []);

  if (!ratings && !reviews && !distance.exists) {
    return <></>;
  }

  const popperContent = strings?.csn_search_venue_card_distance_tooltip || '';

  const distanceLabel = distance.isMetricUnit
    ? strings?.csn_search_venue_card_distance_km || ''
    : strings?.csn_search_venue_card_distance_mi || '';

  const iconLabel = formatMessage(distanceLabel, locale, {
    distance: distance.value
  });

  return (
    <div className="items-center truncate py-1.5 leading-normal text-neutral-60 sm:flex">
      <div className="flex items-center leading-normal text-neutral-60">
        <div
          ref={ratingTriggerRef}
          {...(isListViewInFullMap && {
            onMouseEnter: onMouseEnterRatings,
            onMouseLeave: onMouseLeaveRatings
          })}
        >
          {rating && (
            <Ratings
              rating={rating?.value}
              iconType={rating?.type}
              color={rating?.color}
              screenReaderText={screenReaderText}
              locale={locale}
              // 5.5 rating case
              max={rating?.value > 5 ? 6 : 5}
            />
          )}
        </div>
        {!isListViewInFullMap && ratings && reviews && <div className="mx-2 text-b-md">•</div>}
        {!isListViewInFullMap && reviews && (
          <div className="flex gap-2">
            <div className="text-b-sm/none">Reviews</div>
            {reviews && (
              <Ratings
                rating={Number(reviews.toFixed(1))}
                color="fill-reviews-icon"
                screenReaderText={screenReaderText}
                locale={locale}
              />
            )}
          </div>
        )}

        {isListViewInFullMap && ratingPopperOpen && reviews && (
          <Popper isOpen={ratingPopperOpen} triggerRef={ratingTriggerRef} placement="bottom-start">
            <div className="flex flex-col gap-2 rounded-xl bg-background p-4 shadow-4">
              {rating && (
                <Ratings
                  rating={rating?.value}
                  iconType={rating?.type}
                  color={rating?.color}
                  screenReaderText={screenReaderText}
                  locale={locale}
                  // 5.5 rating case
                  max={rating?.value > 5 ? 6 : 5}
                />
              )}
              {reviews && (
                <div className="flex gap-2">
                  <div className="text-b-sm/none">Reviews</div>
                  {reviews && (
                    <Ratings
                      rating={Number(reviews.toFixed(1))}
                      color="fill-reviews-icon"
                      screenReaderText={screenReaderText}
                      locale={locale}
                    />
                  )}
                </div>
              )}
            </div>
          </Popper>
        )}

        <div className="flex truncate pt-[3px]">
          {showDistance && ratings && distance.exists && <div className="mx-2 text-b-md">•</div>}
          {showDistance && distance.exists && (
            <IconWithPopper iconName="custom-map-pin-2" popperContent={popperContent} iconLabel={iconLabel} />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(RatingReviewDistance);
